import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Spin,
  Alert,
  Button,
  Descriptions,
  message,
  Tag,
  Typography,
} from "antd";
import {
  CreditCardOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const { Title, Text } = Typography;

/**
 * Payment Page Component
 *
 * Flow:
 * 1. User lands here from ERP with URL params (paymentId, amount, businessId)
 * 2. Verify business exists in database
 * 3. Calculate payment breakdown (TDS, GST)
 * 4. Show payment details
 * 5. Initialize payment gateway (NTT Data / Razorpay / etc.)
 */
const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // URL Parameters
  const [paymentId, setPaymentId] = useState(null);
  const [amount, setAmount] = useState(null);
  const [businessId, setBusinessId] = useState(null);

  // State
  const [loading, setLoading] = useState(true);
  const [businessData, setBusinessData] = useState(null);
  const [paymentBreakdown, setPaymentBreakdown] = useState(null);
  const [taxConfiguration, setTaxConfiguration] = useState(null);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Reset payment button if user closes popup and returns to page
  useEffect(() => {
    const handleWindowFocus = () => {
      if (processingPayment) {
        console.log("⚠️ User returned to page - resetting payment button");
        setProcessingPayment(false);
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [processingPayment]);

  // Parse URL parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pidFromUrl = params.get("paymentId");
    const amtFromUrl = params.get("amount");
    const bidFromUrl = params.get("businessId");

    console.log("Payment Page URL Params:", {
      paymentId: pidFromUrl,
      amount: amtFromUrl,
      businessId: bidFromUrl,
    });

    if (!pidFromUrl || !amtFromUrl || !bidFromUrl) {
      setError(
        "Missing required payment parameters (paymentId, amount, businessId)",
      );
      setLoading(false);
      return;
    }

    setPaymentId(pidFromUrl);
    setAmount(parseFloat(amtFromUrl));
    setBusinessId(bidFromUrl);

    // Verify business after params are set
    verifyBusinessAndCalculate(bidFromUrl, parseFloat(amtFromUrl));
  }, [location.search]);

  /**
   * Load ATOM payment script
   */
  useEffect(() => {
    const script = document.createElement("script");
    const scriptVersion = new Date().getTime(); // Cache busting
    script.src = `${process.env.REACT_APP_ATOM_SCRIPT_URL || "https://pgtest.atomtech.in/staticdata/ots/js/atomcheckout.js"}?v=${scriptVersion}`;

    script.onload = () => {
      console.log("✅ ATOM script loaded successfully");
      setScriptLoaded(true);
    };

    script.onerror = () => {
      console.error("❌ Failed to load ATOM payment script");
      message.error("Payment service unavailable. Please try again later.");
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  /**
   * Verify business exists and calculate payment breakdown
   */
  const verifyBusinessAndCalculate = async (bid, amt) => {
    setLoading(true);
    setError(null);

    try {
      // Call backend to verify business and get breakdown
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/verify-payment-business`,
        {
          businessId: bid,
          amount: amt,
        },
      );

      console.log("Business verification response:", response.data);

      if (response.data.status) {
        setBusinessData(response.data.data.business);
        setPaymentBreakdown(response.data.data.breakdown);
        setTaxConfiguration(response.data.data.taxConfiguration || null);

        console.log("Tax Configuration:", response.data.data.taxConfiguration);
        console.log(
          "Active Taxes:",
          response.data.data.taxConfiguration?.allActiveTaxes,
        );
      } else {
        setError(response.data.message || "Failed to verify business");
      }
    } catch (err) {
      console.error("Error verifying business:", err);
      setError(
        err.response?.data?.message ||
          "Failed to verify business. Please contact support.",
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initialize ATOM payment gateway
   */
  const initiatePayment = async () => {
    // Check if ATOM script is loaded
    if (!scriptLoaded) {
      message.error(
        "Payment service is currently unavailable. Please try again in a few minutes or contact support.",
      );
      console.error("ATOM payment script not loaded");
      return;
    }

    setProcessingPayment(true);

    try {
      console.log("🚀 Initiating ATOM payment...");

      // Call backend to generate ATOM token
      // Send totalPayable (with GST) to ATOM, not base amount
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/atom/generate-token`,
        {
          businessId: businessId,
          amount: paymentBreakdown.totalPayable, // Send total with GST
          baseAmount: amount, // Send base amount for reference
          paymentId: paymentId,
        },
      );

      console.log("ATOM Token Response:", response.data);

      if (!response.data.status || !response.data.data.token) {
        throw new Error(
          response.data.message || "Failed to generate payment token",
        );
      }

      const { token, merchId, returnUrl } = response.data.data;
      console.log("businessData in initiatePayment:", businessData);
      // Prepare ATOM payment options
      const atomOptions = {
        atomTokenId: token,
        merchId: merchId,
        custEmail: businessData?.email || "noreply@bharatonline.com",
        custMobile: businessData?.mobileNo || "9999999999",
        amount: paymentBreakdown.totalPayable,
        returnUrl: returnUrl,
      };

      console.log("ATOM Options:", atomOptions);

      // Open ATOM payment popup
      if (window.AtomPaynetz) {
        message.info("Opening payment gateway...");
        new window.AtomPaynetz(atomOptions, "uat");

        // Reset button state after popup opens (2 seconds delay)
        // User can close popup and retry payment
        setTimeout(() => {
          setProcessingPayment(false);
          console.log("✓ Payment gateway opened, button re-enabled");
        }, 2000);
      } else {
        throw new Error("ATOM Payment gateway not initialized");
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      message.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to initiate payment. Please try again.",
      );
      setProcessingPayment(false);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f7fb",
        }}
      >
        <Spin size="large" tip="Verifying payment details..." />
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#f5f7fb",
          padding: "20px",
        }}
      >
        <Card style={{ maxWidth: 600, width: "100%" }}>
          <div style={{ textAlign: "center" }}>
            <CloseCircleOutlined
              style={{ fontSize: 64, color: "#ff4d4f", marginBottom: 16 }}
            />
            <Title level={3}>Payment Error</Title>
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ marginTop: 16, textAlign: "left" }}
            />
            <Button
              type="default"
              size="large"
              style={{ marginTop: 24 }}
              onClick={() => navigate("/")}
            >
              Go to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Payment Page
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f0f2f5",
        padding: "12px 16px",
      }}
    >
      <Row justify="center">
        <Col xs={24} sm={22} md={20} lg={18} xl={14}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <ShoppingOutlined style={{ fontSize: 38, color: "#1890ff" }} />
            <Title level={3} style={{ marginTop: 6, marginBottom: 4 }}>
              WhatsApp Credit Recharge
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Review and complete your payment securely
            </Text>
            <div style={{ marginTop: 6, fontSize: 12, color: "#8c8c8c" }}>
              Transaction ID:{" "}
              <Text code style={{ fontSize: 11 }}>
                {paymentId}
              </Text>
            </div>
          </div>

          {/* Business Info Card */}
          <Card
            size="small"
            title={
              <span style={{ fontSize: 14 }}>
                <CheckCircleOutlined
                  style={{ color: "#52c41a", marginRight: 6, fontSize: 14 }}
                />
                Account Details
              </span>
            }
            style={{ marginBottom: 12 }}
            bodyStyle={{ padding: "12px 16px" }}
          >
            <Descriptions column={1} size="small" colon={false}>
              <Descriptions.Item
                label="Entity Name"
                labelStyle={{ fontSize: 12, width: 120 }}
                contentStyle={{ fontSize: 13 }}
              >
                <Text strong>{businessData?.businessName || "N/A"}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                label="Email"
                labelStyle={{ fontSize: 12, width: 120 }}
                contentStyle={{ fontSize: 13 }}
              >
                <Text>{businessData?.email || "Not provided"}</Text>
              </Descriptions.Item>
              <Descriptions.Item
                label="Status"
                labelStyle={{ fontSize: 12, width: 120 }}
                contentStyle={{ fontSize: 13 }}
              >
                <Tag color="success" style={{ fontSize: 11 }}>
                  VERIFIED
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Payment Summary - Compact */}
          <Card
            size="small"
            title={
              <span style={{ fontSize: 14 }}>
                <CreditCardOutlined style={{ marginRight: 6, fontSize: 14 }} />
                Payment Summary
              </span>
            }
            style={{ marginBottom: 12 }}
            bodyStyle={{ padding: "16px" }}
          >
            {/* Breakdown Table */}
            <table style={{ width: "100%", fontSize: 13, marginBottom: 12 }}>
              <tbody>
                <tr>
                  <td style={{ padding: "6px 0", color: "#595959" }}>
                    Base Amount
                  </td>
                  <td
                    style={{
                      padding: "6px 0",
                      textAlign: "right",
                      fontWeight: 500,
                    }}
                  >
                    ₹{paymentBreakdown?.baseAmount?.toFixed(2) || "0.00"}
                  </td>
                </tr>

                {/* Conditional TDS Row - Only show if TDS enabled and amount > 0 */}
                {paymentBreakdown?.tdsAmount > 0 && (
                  <>
                    <tr>
                      <td
                        style={{
                          padding: "6px 0",
                          color: "#8c8c8c",
                          fontSize: 12,
                        }}
                      >
                        Less: TDS ({paymentBreakdown?.tdsPercentage || 0}%)
                      </td>
                      <td
                        style={{
                          padding: "6px 0",
                          textAlign: "right",
                          color: "#ff4d4f",
                          fontSize: 12,
                        }}
                      >
                        -₹{paymentBreakdown?.tdsAmount?.toFixed(2) || "0.00"}
                      </td>
                    </tr>
                    {paymentBreakdown?.tanNumber && (
                      <tr>
                        <td
                          colSpan="2"
                          style={{
                            padding: "2px 0 6px 8px",
                            color: "#8c8c8c",
                            fontSize: 10,
                          }}
                        >
                          TAN: {paymentBreakdown.tanNumber}
                        </td>
                      </tr>
                    )}
                  </>
                )}

                <tr>
                  <td style={{ padding: "6px 0", color: "#595959" }}>
                    Net Amount
                  </td>
                  <td style={{ padding: "6px 0", textAlign: "right" }}>
                    ₹{paymentBreakdown?.netAmount?.toFixed(2) || "0.00"}
                  </td>
                </tr>

                {/* Display all GST taxes from tax breakdown */}
                {paymentBreakdown?.taxBreakdown &&
                  paymentBreakdown.taxBreakdown
                    .filter((tax) => !tax.isDeduction)
                    .map((tax, index) => (
                      <tr key={index}>
                        <td
                          style={{
                            padding: "6px 0",
                            color: "#8c8c8c",
                            fontSize: 12,
                          }}
                        >
                          Add: {tax.name} ({tax.percentage}%)
                        </td>
                        <td
                          style={{
                            padding: "6px 0",
                            textAlign: "right",
                            color: "#faad14",
                            fontSize: 12,
                          }}
                        >
                          +₹{tax.amount?.toFixed(2) || "0.00"}
                        </td>
                      </tr>
                    ))}

                {/* Fallback to single GST if no breakdown */}
                {(!paymentBreakdown?.taxBreakdown ||
                  paymentBreakdown.taxBreakdown.filter((t) => !t.isDeduction)
                    .length === 0) && (
                  <tr>
                    <td
                      style={{
                        padding: "6px 0",
                        color: "#8c8c8c",
                        fontSize: 12,
                      }}
                    >
                      Add: GST ({paymentBreakdown?.gstPercentage || 0}%)
                    </td>
                    <td
                      style={{
                        padding: "6px 0",
                        textAlign: "right",
                        color: "#faad14",
                        fontSize: 12,
                      }}
                    >
                      +₹{paymentBreakdown?.gstAmount?.toFixed(2) || "0.00"}
                    </td>
                  </tr>
                )}

                <tr style={{ borderTop: "2px solid #d9d9d9" }}>
                  <td
                    style={{
                      padding: "10px 0 6px",
                      fontWeight: 600,
                      fontSize: 15,
                    }}
                  >
                    Total Payable
                  </td>
                  <td
                    style={{
                      padding: "10px 0 6px",
                      textAlign: "right",
                      fontWeight: 600,
                      fontSize: 17,
                      color: "#1890ff",
                    }}
                  >
                    ₹{paymentBreakdown?.totalPayable?.toFixed(2) || "0.00"}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Credits Info */}
            <div
              style={{
                background: "#f0f5ff",
                padding: "10px 12px",
                borderRadius: 4,
                border: "1px solid #d6e4ff",
              }}
            >
              <Row justify="space-between" align="middle">
                <Col>
                  <Text style={{ fontSize: 13, color: "#1890ff" }}>
                    💳 Credits to be Added
                  </Text>
                </Col>
                <Col>
                  <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                    {paymentBreakdown?.creditsAdded?.toFixed(2) || "0.00"}
                  </Text>
                </Col>
              </Row>
              <Text
                style={{
                  fontSize: 11,
                  color: "#595959",
                  display: "block",
                  marginTop: 4,
                }}
              >
                Credits = Net Amount
              </Text>
            </div>
          </Card>

          {/* Payment Action Section - Info + Button Side by Side */}
          <Card
            size="small"
            style={{ marginBottom: 12 }}
            bodyStyle={{ padding: "16px" }}
          >
            <Row gutter={[16, 16]}>
              {/* Left: Important Information */}
              <Col xs={24} md={14}>
                <div
                  style={{
                    background: "#e6f7ff",
                    padding: "12px",
                    borderRadius: 4,
                    border: "1px solid #91d5ff",
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      marginBottom: 8,
                      color: "#1890ff",
                    }}
                  >
                    ℹ️ Important Information
                  </div>
                  <div style={{ fontSize: 12, lineHeight: "1.6" }}>
                    {/* <div>✓ Secured by ATOM Payment Gateway (NTT Data)</div> */}
                    {/* <div>✓ TDS deducted as per Income Tax regulations</div> */}
                    <div>✓ GST invoice generated automatically</div>
                    <div>✓ Credits added instantly after payment</div>
                  </div>
                </div>
              </Col>

              {/* Right: Payment Button */}
              <Col xs={24} md={10}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    type="primary"
                    size="large"
                    block
                    icon={<CreditCardOutlined />}
                    loading={processingPayment}
                    onClick={initiatePayment}
                    style={{ height: 52, fontSize: 16, fontWeight: 600 }}
                  >
                    {processingPayment
                      ? "Processing..."
                      : `Pay ₹${paymentBreakdown?.totalPayable?.toFixed(2) || "0.00"}`}
                  </Button>
                  <div style={{ textAlign: "center", marginTop: 8 }}>
                    {/* <Text type="secondary" style={{ fontSize: 11 }}>
                      🔒 SSL Encrypted • PCI DSS Compliant
                    </Text> */}
                  </div>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Footer */}
          <div style={{ textAlign: "center", marginTop: 12, marginBottom: 16 }}>
            {/* <Text type="secondary" style={{ fontSize: 12 }}>
              Need help? Contact <Text strong>support@bharatonline.com</Text>
            </Text> */}
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Transaction Time: {new Date().toLocaleString()}
              </Text>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default PaymentPage;
